import React, { useState, useEffect, useRef } from 'react';
import '../styles/CreateAppointmentModal.css';
import { getAuthHeaders } from '../../utils/auth';
import { createPortal } from 'react-dom';
import { STATUS_OPTIONS, APPOINTMENT_TITLES} from '../../constants/calendar';
import { X, Calendar, Clock, User, Phone, FileText, AlertCircle, CheckCircle, ExternalLink, ShoppingBag, CalendarCheck, PhoneCall, Ban, CalendarSync, CreditCard } from 'lucide-react';
import SaveCardModal from '../WorkOrderProfile/SaveCardModal';
import { buildUrl , API_URL} from '../../config/api';
import { useBatchedOrderBalances, BatchedTotalBalanceDisplay } from '../hooks/useOrderBalance';
import { AppointmentService } from '../../services/appointmentService';
import { getCurrentUser } from '../../utils/auth';
import { toLocalDateTime, localDateTimeToUTC, formatDateTimeForUser, 
  isValidDate, parseDate, addHours, toAPIDateTime } from '../../utils/dateUtils';
import { isIsraelUser } from '../../utils/locationUtils';
import CustomDateTimePicker from './CustomDateTimePicker';

// Users allowed to book Dini at 3 PM or later (must match dinitracker-api/routes/appointments.js)
const LATE_DINI_ALLOWED_USERS = ['gkirsh', 'didip', 'chenw'];
// currentUser is a username string in most callers, a user object in a few
const isLateDiniAllowedUser = (user) => {
  const name = typeof user === 'object' ? (user?.UserName || user?.username) : user;
  return LATE_DINI_ALLOWED_USERS.includes(String(name || '').trim().toLowerCase());
};

const CreateAppointmentModal = ({
  isOpen,
  onClose,
  customerInfo = null,
  currentUser = getCurrentUser(),
  prefillData = {},
  onAppointmentCreated = () => {},
   onAppointmentUpdated = () => {},
   onAppointmentDeleted = () => {},
  customTitle = 'Create New Appointment',
  editingAppointment = null,
  woeMode = false, // When true: orderno not required (will be linked after order submission)
  onLoginRequired = null
}) => {
  // Form state
  const [formData, setFormData] = useState({
    EventDesc: '',
    ClientName: '',
    PhoneNum: '',
    ClientID: '',
    Title: '',
    EventStart: '',
    EventEnd: '',
    MeetingWith: '',
    MWUserId: '',
    AppointmentNotes: '',
    bDiniCare: 0,
    isDiniReq: false,
    isReadyToPickup: false,
    orderNo: '',
    workerResponsible: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', null
  const [errorMessage, setErrorMessage] = useState('');
  const [noCardError, setNoCardError] = useState(false);
  const [bypassCardCheck, setBypassCardCheck] = useState(false);
  const [showSaveCardModal, setShowSaveCardModal] = useState(false);
  const isEditing = editingAppointment !== null;
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeField, setActiveField] = useState(null); // 'name' or 'phone'
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [customerOrders, setCustomerOrders] = useState([]);
  const [sendOutOrders, setSendOutOrders] = useState([]);
  const [isLoadingSendOutOrders, setIsLoadingSendOutOrders] = useState(false);
  const [distributorUsers, setDistributorUsers] = useState([]);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [customerNotes, setCustomerNotes] = useState('');

  // Check if user is from Israel to use military time
  const useMilitaryTime = isIsraelUser();







  // Meeting With options - staff members with their IDs
    const meetingWithOptions = [
    { name: 'Dini', id: 41 }, //pomona
    { name: 'Joselyn', id: 48 }, //pomona
    // { name: 'Sari Wolbe', id: 147 },//pomona
    { name: 'Yael', id: 162}, //pomona
    { name: 'Tania', id: 89 },//pomona
    { name: 'Tehila', id: 205 },//pomona
    { name: 'Devorah', id: 328 }, //pomona
    { name: 'Soraya', id: 115}, 
    { name: 'Send Out', id: 213 },//pomona
    { name: 'Wash and Set', id: 282 },//pomona
    { name: 'Batya', id: 257 }, //lakewood
    { name: 'Eti', id: 207 }, //lakewood
    { name: 'Mindy', id: 288 }, //lakewood
    { name: 'Chay', id: 306 },  //lakewood
    { name: 'Michal', id: 327 }, //lakewood
    { name: 'Send Out LW', id: 307 }, //lakewood aka svetlana
    { name: 'Yafa', id: 127 }, //israel
    { name: 'Ayala', id: 129 }, //israel
    { name: 'Hanna', id: 223},
    { name: 'Zehavi', id: 226},
    { name: 'MalkiFriedman', id: 254 } //israel
    ];

    const customerBalances = useBatchedOrderBalances(
      customerOrders, 
      customerOrders.length > 0
    );

    const handleViewCustomer = () => {
      if (formData.ClientID) {
        window.open(`/customers/${formData.ClientID}`, '_blank');
      }
    };

    const handleOpenSaveCardModal = () => {
      setShowSaveCardModal(true);
    };



const handleCancelAppointment = async () => {
  if (!isEditing || !editingAppointment?.ID) {
    return;
  }

  setIsDeleting(true);
  setErrorMessage('');

  try {
    const response = await fetch(buildUrl(`/api/appointments/${editingAppointment.ID}/status`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify({ status: 'Cancelled' })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const err = new Error(errorData.message || 'Failed to cancel appointment');
      err.status = response.status;
      throw err;
    }

    const result = await response.json();
    
    // Update the appointment object with cancelled status
    const cancelledAppointment = {
      ...editingAppointment,
      AppointmentStatus: 'Cancelled'
    };
    
    // Call the update callback
    onAppointmentUpdated(cancelledAppointment);
    
    // Close modal
    onClose();

  } catch (error) {
    if (error.status === 401 && onLoginRequired) {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
      onLoginRequired(() => handleCancelAppointment());
      return;
    }
    console.error('Error cancelling appointment:', error);
    setErrorMessage(error.message || 'An error occurred while cancelling the appointment');
  } finally {
    setIsDeleting(false);
    setShowDeleteConfirm(false);
  }
};

const handleStatusChange = async (newStatus) => {
  if (!editingAppointment?.ID) return;
  
  setUpdatingStatus(true);
  
  try {
    const response = await fetch(buildUrl(`/api/appointments/${editingAppointment.ID}/status`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify({ status: newStatus })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const err = new Error(errorData.message || 'Failed to update status');
      err.status = response.status;
      throw err;
    }
    
    // Update the formData state
    setFormData(prev => ({
      ...prev,
      AppointmentStatus: newStatus
    }));
    
    // Create the updated appointment object
    const updatedAppointment = {
      ...editingAppointment,
      ...formData,
      AppointmentStatus: newStatus
    };
    
    // Call the update callback
    if (onAppointmentUpdated) {
      onAppointmentUpdated(updatedAppointment);
    }
    
    // Show success in your existing error message state (temporarily)
    setErrorMessage(''); // Clear any existing errors
    
  } catch (error) {
    if (error.status === 401 && onLoginRequired) {
      setUpdatingStatus(false);
      setShowStatusDropdown(false);
      onLoginRequired(() => handleStatusChange(newStatus));
      return;
    }
    console.error('Error updating status:', error);
    setErrorMessage('Failed to update status');
  } finally {
    setUpdatingStatus(false);
    setShowStatusDropdown(false);
  }
};

// Replace the convertToLocalDatetimeString function with this:
const convertToLocalDatetimeString = (datetimeString) => {
  if (!datetimeString) return '';
  
  // If already in correct format, return as-is
  if (datetimeString.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/)) {
    return datetimeString;
  }
  
  return toLocalDateTime(datetimeString);
};
// Helper function to build initial form data
const buildInitialFormData = ({ customerInfo, prefillData, editingAppointment, isEditing }) => {
  if (isEditing && editingAppointment) {
    return {
      EventDesc: editingAppointment.EventDesc || '',
      ClientName: editingAppointment.ClientName || '',
      PhoneNum: editingAppointment.PhoneNum || '',
      ClientID: editingAppointment.ClientID || '',
      Title: editingAppointment.Title || '',
      EventStart: convertToLocalDatetimeString(editingAppointment.EventStart),
      EventEnd: convertToLocalDatetimeString(editingAppointment.EventEnd),
      MeetingWith: editingAppointment.MeetingWith || '',
      MWuserID: editingAppointment.MWuserID || '',
      AppointmentNotes: editingAppointment.AppointmentNotes || '',
      AppointmentStatus: editingAppointment.AppointmentStatus || 'Booked',
      bDiniCare: prefillData.bDiniCare || editingAppointment.bDiniCare || 0,
      isDiniReq: editingAppointment.isDiniReq || false,
      isReadyToPickup: editingAppointment.isReadyToPickup || false,
      orderNo: editingAppointment.orderno ? String(editingAppointment.orderno) : '',
      workerResponsible: editingAppointment.checkedinby || ''
    };
  } else {
    return {
      EventDesc: prefillData.EventDesc || '',
      ClientName: customerInfo?.customerName || prefillData.ClientName || '',
      PhoneNum: customerInfo?.phone || prefillData.PhoneNum || '',
      ClientID: customerInfo?.customerNo || prefillData.ClientID || '',
      Title: prefillData.Title || '',
      EventStart: prefillData.EventStart || '',
      EventEnd: prefillData.EventEnd || '',
      MeetingWith: prefillData.MeetingWith || '',
      MWuserID: prefillData.MWuserID || '',
      AppointmentNotes: prefillData.AppointmentNotes || '',
      AppointmentStatus: prefillData.AppointmentStatus || 'Booked',
      bDiniCare: customerInfo?.bDiniCare || prefillData.bDiniCare || 0,
      isDiniReq: prefillData.isDiniReq || false,
      isReadyToPickup: prefillData.isReadyToPickup || false,
      orderNo: '',
      workerResponsible: ''
    };
  }
};

// Ref with to track initialization
const hasInitializedRef = useRef(false);

useEffect(() => {
  if (!isOpen) {
    hasInitializedRef.current = false; // reset when modal closes
    return;
  }

  if (hasInitializedRef.current) return; // prevent re-initialization while modal is open

  const initializeFormData = async () => {
    let initialData = buildInitialFormData({
      customerInfo,
      prefillData,
      editingAppointment,
      isEditing
    });

    // If we have a ClientID from prefillData but no customerInfo, fetch the customer details
    console.log('🔍 DEBUG - Checking prefill conditions:', {
      isEditing,
      hasClientID: !!prefillData.ClientID,
      ClientID: prefillData.ClientID,
      hasCustomerInfo: !!customerInfo,
      prefillData,
      customerInfo
    });

    if (!isEditing && prefillData.ClientID && !customerInfo) {
      try {
        const customerData = await AppointmentService.fetchCustomerById(prefillData.ClientID);

        if (customerData) {
          // Update initial data with fetched customer info
          initialData = {
            ...initialData,
            ClientName: customerData.CustomerFullName ||
                       `${customerData.CustomerFirstName || ''} ${customerData.CustomerLastName || ''}`.trim(),
            PhoneNum: customerData.CustomerPhone || '',
            bDiniCare: customerData.bDiniCare || 0
          };
        } else {
          console.warn('⚠️ No customer data returned from API');
        }
      } catch (error) {
        console.error('❌ Error fetching customer data for prefill:', error);
      }
    } else {
      console.log('⚠️ Skipping customer fetch - conditions not met');
    }

    setFormData(initialData);
    setSubmitStatus(null);
    setErrorMessage('');
  };

  initializeFormData();
  hasInitializedRef.current = true;
}, [isOpen, customerInfo, prefillData, editingAppointment, isEditing]);


// Fetch distributor users when Send Out is selected
useEffect(() => {
  if (formData.Title === 'Send Out' && distributorUsers.length === 0) {
    AppointmentService.fetchDistributorUsers()
      .then(users => setDistributorUsers(users))
      .catch(() => setDistributorUsers([]));
  }
}, [formData.Title]);

// Fetch order numbers for order dropdown when ClientID changes
useEffect(() => {
  if (formData.ClientID) {
    setIsLoadingSendOutOrders(true);
    AppointmentService.fetchCustomerOrders(formData.ClientID)
      .then(orders => setSendOutOrders(orders))
      .catch(() => setSendOutOrders([]))
      .finally(() => setIsLoadingSendOutOrders(false));
  } else {
    setSendOutOrders([]);
  }
}, [formData.ClientID]);

// Fetch customer orders and notes when ClientID changes
  useEffect(() => {
  const fetchCustomerData = async () => {
    if (formData.ClientID) {
      try {
        // Fetch orders
        const ordersResponse = await fetch(`${API_URL}/api/customers/${formData.ClientID}/orders`);
        if (ordersResponse.ok) {
          const data = await ordersResponse.json();
          const orders = data.orders || [];
          console.log('📦 Orders received:', orders.length, 'orders');
          setCustomerOrders(orders);
        } else {
          console.log('📦 Orders fetch failed:', ordersResponse.status);
        }

        // Fetch customer details including notes
        const customerData = await AppointmentService.fetchCustomerById(formData.ClientID);
        if (customerData && customerData.CustomerNotes) {
          setCustomerNotes(customerData.CustomerNotes);
        } else {
          setCustomerNotes('');
        }
      } catch (error) {
        console.error('Error fetching customer data:', error);
        setCustomerOrders([]);
        setCustomerNotes('');
      }
    } else {
      setCustomerOrders([]);
      setCustomerNotes('');
    }
  };

  fetchCustomerData();
}, [formData.ClientID, isOpen]);


useEffect(() => {
  const handleClickOutside = (event) => {
    if (!event.target.closest('.modal-status-container')) {
      setShowStatusDropdown(false);
    }
  };

  document.addEventListener('mousedown', handleClickOutside);
  return () => {
    document.removeEventListener('mousedown', handleClickOutside);
  };
}, []); 


const handleStartTimeChange = (startDateTime) => {
  setFormData(prev => {
    const newData = { ...prev, EventStart: startDateTime };
    
    // Auto-set end time if not already set
    if (startDateTime && !prev.EventEnd) {
      const endTime = addHours(startDateTime, 1);
      newData.EventEnd = toLocalDateTime(endTime);
    }
    
    return newData;
  });
};

  const handleMeetingWithChange = (selectedName) => {
  const selectedOption = meetingWithOptions.find(option => option.name === selectedName);
  setFormData(prev => ({
    ...prev,
    MeetingWith: selectedName,
    MWuserID: selectedOption ? selectedOption.id : ''
  }));
};

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

const searchCustomers = async (searchTerm) => {
  if (!searchTerm || searchTerm.length < 3) {
    setCustomerSuggestions([]);
    setShowSuggestions(false);
    return;
  }

  setIsLoadingSuggestions(true);
  try {
    const response = await fetch(buildUrl(`/api/customers/search?term=${encodeURIComponent(searchTerm)}`));
    if (response.ok) {
      const customers = await response.json();
      setCustomerSuggestions(customers);
      setShowSuggestions(customers.length > 0);
    }
  } catch (error) {
    console.error('Error searching customers:', error);
    setCustomerSuggestions([]);
    setShowSuggestions(false);
  } finally {
    setIsLoadingSuggestions(false);
  }
};

const handleCustomerSelect = (customer) => {
  setFormData(prev => ({
    ...prev,
    ClientName: customer.CustomerFullName || `${customer.CustomerFirstName} ${customer.CustomerLastName}`.trim(),
    PhoneNum: customer.CustomerPhone || '',
    ClientID: customer.ID || '',
    bDiniCare: customer.bDiniCare || 0
  }));
  setShowSuggestions(false);
  setCustomerSuggestions([]);
  setActiveField(null);
};

useEffect(() => {
  const timeoutId = setTimeout(() => {
    if (activeField === 'name' && formData.ClientName) {
      searchCustomers(formData.ClientName);
    } else if (activeField === 'phone' && formData.PhoneNum) {
      searchCustomers(formData.PhoneNum);
    }
  }, 300); // 300ms debounce

  return () => clearTimeout(timeoutId);
}, [formData.ClientName, formData.PhoneNum, activeField]);

const saveCustomerLocally = async (customerData) => {
  try {
    console.log('💾 Saving customer to local DB...');

    // Parse the customer name
    const nameParts = (customerData.clientName || '').trim().split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    // Clean phone number consistently
    const cleanPhone = (customerData.phone || '').replace(/\D/g, '');

    const requestBody = {
      CustomerFirstName: firstName,
      CustomerLastName: lastName,
      CustomerPhone: cleanPhone,
      CustomerEmail: customerData.email || '',
      CustomerAddress: '',
      CustomerCity: '',
      CustomerState: '',
      CustomerZipCode: '',
      MoldId: '',
    };

    console.log('📤 Sending customer to API:', requestBody);

    const response = await fetch(`${API_URL}/api/customers`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to save customer: ${response.status}`);
    }

    const result = await response.json();
    console.log('✅ Customer saved locally:', { customerId: result.customerId });

    return {
      success: true,
      customerId: result.customerId,
      isNew: result.isNewCustomer,
      action: result.isNewCustomer ? 'created' : 'updated'
    };

  } catch (error) {
    console.error('❌ Failed to save customer locally:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

const validateForm = () => {
  const required = ['EventDesc', 'MeetingWith', 'ClientName', 'Title', 'EventStart', 'EventEnd'];
  const missing = required.filter(field => {
    const value = formData[field];
    return !value || (typeof value === 'string' && !value.trim());
  });

  // Check if Title is selected and is a valid option
  if (!formData.Title || !APPOINTMENT_TITLES.includes(formData.Title)) {
    setErrorMessage('Please select a valid appointment type from the dropdown');
    return false;
  }

  if (missing.length > 0) {
    setErrorMessage(`Please fill in required fields: ${missing.join(', ')}`);
    return false;
  }

  // Send Out specific validation
  if (formData.Title === 'Send Out') {
    if (!formData.ClientID) {
      setErrorMessage('A customer must be selected (with a Customer ID) for Send Out appointments');
      return false;
    }
    if (!formData.workerResponsible || !formData.workerResponsible.trim()) {
      setErrorMessage('Worker responsible for shipping is required for Send Out appointments');
      return false;
    }
  }

  // Validate phone number is required when creating new customer
  if (!formData.ClientID && !formData.PhoneNum) {
    setErrorMessage('Phone number is required when creating a new customer');
    return false;
  }

  // Validate dates
  const startDate = parseDate(formData.EventStart);
  const endDate = parseDate(formData.EventEnd);

  if (!startDate || !endDate) {
    setErrorMessage('Please enter valid start and end times');
    return false;
  }

  if (endDate <= startDate) {
    setErrorMessage('End time must be after start time');
    return false;
  }

  // Block late Dini bookings unless the slot is unchanged from the saved appointment
  const isDini = Number(formData.MWuserID) === 41 || formData.MeetingWith === 'Dini';
  if (isDini && startDate.getHours() >= 15 && !isLateDiniAllowedUser(currentUser)) {
    const originalStart = isEditing ? parseDate(editingAppointment?.EventStart) : null;
    const wasDini = Number(editingAppointment?.MWuserID) === 41 || editingAppointment?.MeetingWith === 'Dini';
    const slotUnchanged = wasDini && originalStart && originalStart.getTime() === startDate.getTime();
    if (!slotUnchanged) {
      setErrorMessage("Dini isn't available after 3 PM. Only GKirsh, DidiP, or ChenW can book Dini at this time.");
      return false;
    }
  }

  return true;
};

// Updated handleSubmit function for your CreateAppointmentModal component

const handleSubmit = async () => {
  if (!validateForm()) {
    return;
  }

  setIsSubmitting(true);
  setErrorMessage('');
  setNoCardError(false);

  try {
    let clientId = formData.ClientID;

    // If no ClientID exists, create a new customer first
    if (!clientId && formData.ClientName && formData.PhoneNum) {
      console.log('No customer ID found, creating new customer...');

      const customerSaveResult = await saveCustomerLocally({
        clientName: formData.ClientName,
        phone: formData.PhoneNum,
        email: '' // Could add email field later if needed
      });

      if (!customerSaveResult.success) {
        throw new Error(`Failed to create customer: ${customerSaveResult.error}`);
      }

      clientId = customerSaveResult.customerId;
      console.log(`✅ New customer created with ID: ${clientId}`);

      // Update the form data with the new customer ID
      setFormData(prev => ({ ...prev, ClientID: clientId }));
    }

    const appointmentData = {
      ...formData,
      ClientID: clientId, // Use the clientId (either existing or newly created)
      EventStart: localDateTimeToUTC(formData.EventStart),
      EventEnd: localDateTimeToUTC(formData.EventEnd),
      EnteredBy: currentUser,
      AppointmentStatus: formData.AppointmentStatus || 'Booked',
      orderno: formData.orderNo || null,
      checkedinby: formData.Title === 'Send Out' ? formData.workerResponsible : null,
      bypassCardCheck
    };

    let result;

    if (isEditing) {
      // For editing, we need to check if bDiniCare changed and update customer if needed
      const originalBDiniCare = editingAppointment?.bDiniCare || 0;
      const newBDiniCare = appointmentData.bDiniCare;

      // Update customer's bDiniCare if it changed and we have a customer ID
      if (appointmentData.ClientID && originalBDiniCare !== newBDiniCare) {
        try {
          console.log('Updating customer Dini Care status...');
          await AppointmentService.updateCustomerDiniCare(appointmentData.ClientID, newBDiniCare);
          console.log('Customer Dini Care updated successfully');
        } catch (customerError) {
          console.error('Failed to update customer Dini Care:', customerError);
          // Show warning but don't fail the appointment update
          setErrorMessage(`Warning: Appointment updated but failed to update customer Dini Care status: ${customerError.message}`);
        }
      }

      // Update the appointment
      result = await AppointmentService.updateAppointment(editingAppointment.ID, appointmentData);
    } else {
      // For new appointments, create the appointment first
      result = await AppointmentService.createAppointment(appointmentData);

      // Then update customer's bDiniCare if we have a customer ID and bDiniCare is set
      if (appointmentData.ClientID && appointmentData.bDiniCare === 1) {
        try {
          console.log('Setting customer Dini Care status for new appointment...');
          await AppointmentService.updateCustomerDiniCare(appointmentData.ClientID, 1);
          console.log('Customer Dini Care updated successfully');
        } catch (customerError) {
          console.error('Failed to update customer Dini Care:', customerError);
          // Show warning but don't fail since appointment was created
          setErrorMessage(`Appointment created successfully but failed to update customer Dini Care status: ${customerError.message}`);
        }
      }
    }

    setSubmitStatus('success');

    // If card check was bypassed, fire a reminder to add the card later
    if (bypassCardCheck && formData.ClientID && formData.ClientName) {
      window.dispatchEvent(new CustomEvent('cardBypassReminder', {
        detail: { clientId: formData.ClientID, clientName: formData.ClientName }
      }));
    }

    // Call appropriate callback
    if (isEditing) {
      onAppointmentUpdated(result.appointment);
    } else {
      onAppointmentCreated(result.appointment);
    }

    // Close modal after short delay to show success message
    setTimeout(() => {
      onClose();
      setSubmitStatus(null);
    }, 1500);

  } catch (error) {
    console.error(`Error ${isEditing ? 'updating' : 'creating'} appointment:`, error);

    if (error.status === 401 && onLoginRequired) {
      setIsSubmitting(false);
      onLoginRequired(() => handleSubmit());
      return;
    }

    setSubmitStatus('error');
    const msg = error.message || `An error occurred while ${isEditing ? 'updating' : 'creating'} the appointment`;
    if (msg.toLowerCase().includes('no card on file')) {
      setNoCardError(true);
      setErrorMessage('');
    } else {
      setNoCardError(false);
      setErrorMessage(msg);
    }
  } finally {
    setIsSubmitting(false);
  }
};

// Also add this helper function to handle bDiniCare changes in real-time
const handleDiniCareChange = async (checked) => {
  const newValue = checked ? 1 : 0;
  
  // Update form data immediately for UI responsiveness
  handleInputChange('bDiniCare', newValue);
  
  // If we're editing and have a customer ID, update the customer record immediately
  if (isEditing && formData.ClientID) {
    try {
      console.log('Updating customer Dini Care status in real-time...');
      await AppointmentService.updateCustomerDiniCare(formData.ClientID, newValue);
      console.log('Customer Dini Care updated successfully');
      
      // Optionally show a brief success indicator
      // You could add a small success message or checkmark
      
    } catch (error) {
      console.error('Failed to update customer Dini Care:', error);
      // Revert the change in the UI
      handleInputChange('bDiniCare', checked ? 0 : 1);
      setErrorMessage(`Failed to update customer Dini Care status: ${error.message}`);
    }
  }
};
  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  // Non-blocking heads-up when the chosen start date has already passed.
  // Purely informational — it never prevents the appointment from being saved.
  const isPastAppointment = (() => {
    const start = parseDate(formData.EventStart);
    if (!start) return false;
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    return start < startOfToday;
  })();

  // Dini doesn't take appointments starting at 3 PM or later — blocked for
  // everyone except LATE_DINI_ALLOWED_USERS (server enforces the same rule).
  const isLateDiniAppointment = (() => {
    const isDini = Number(formData.MWuserID) === 41 || formData.MeetingWith === 'Dini';
    if (!isDini) return false;
    const start = parseDate(formData.EventStart);
    return !!start && start.getHours() >= 15;
  })();
  const canBookLateDini = isLateDiniAllowedUser(currentUser);


  if (!isOpen) return null;
  


  // Use createPortal to render modal at document.body level
  return [createPortal(
  <div className="appointment-modal-overlay">
    <div className="appointment-modal-content" onClick={e => e.stopPropagation()}>
      {/* Header */}
      <div className="appointment-modal-header">
        
        <h2 className="appointment-modal-title">{customTitle}</h2>
         {/* Show pickup icon when Ready to Pickup is checked */}

     
        {isEditing && (
          <div className="modal-status-container" style={{ position: 'relative', marginLeft: 'auto', marginRight: '15px' }}>
            <span
              className={`status-badge ${formData.Title !== 'Send Out' ? 'clickable' : ''} ${(formData.AppointmentStatus || 'Booked').toLowerCase().replace(/\s+/g, '-')}`}
              onClick={() => formData.Title !== 'Send Out' && setShowStatusDropdown(!showStatusDropdown)}
              title={formData.Title === 'Send Out' ? 'Status is managed automatically for Send Out appointments' : undefined}
            >
              {formData.AppointmentStatus || 'Pending'}
            </span>

            {showStatusDropdown && formData.Title !== 'Send Out' && (
              <div className="status-dropdown">
                {STATUS_OPTIONS.map(status => (
                  <button
                    key={status}
                    className={`status-dropdown-option ${status === (formData.AppointmentStatus || 'Pending') ? 'selected' : ''}`}
                    onClick={() => handleStatusChange(status)}
                    disabled={updatingStatus}
                  >
                    <span className={`status-preview ${status.toLowerCase().replace(/\s+/g, '-')}`}></span>
                    {status}
                  </button>
                ))}
              </div>
            )}
            
          </div>
          
        )}
   
        <button 
          className="appointment-modal-close-btn" 
          onClick={handleClose}
          disabled={isSubmitting}
        >
          <X size={24} />
        </button>
      </div>

        {/* Form */}
        <div className="appointment-form">
          <div className="appointment-form-grid">
{(formData.bDiniCare === 1 || formData.bDiniCare === true) ? (
  <div className="appointment-form-group full-width">
    <div className="dini-care-layout">
      {/* Left side - Form fields */}
      <div className="dini-care-fields">
        {/* Event Description */}
        <div className="appointment-form-group">
          <label className="appointment-form-label required">
            <FileText size={16} />
            Event Description
          </label>
          <textarea
            className="appointment-form-textarea"
            value={formData.EventDesc}
            onChange={(e) => handleInputChange('EventDesc', e.target.value)}
            placeholder="Brief description of the appointment"
            rows={2}
          />
        </div>

        {/* Client Name */}
        <div className="appointment-form-group" style={{ position: 'relative' }}>
          <label className="appointment-form-label required">
            <User size={16} />
            Client Name
          </label>
          <input
            type="text"
            className="appointment-form-input"
            value={formData.ClientName}
            onChange={(e) => {
              setFormData(prev => ({ ...prev, ClientName: e.target.value, ClientID: '', PhoneNum: '' }));
              setActiveField('name');
            }}
            onBlur={() => {
              setTimeout(() => {
                setShowSuggestions(false);
                setActiveField(null);
              }, 200);
            }}
            onFocus={() => setActiveField('name')}
            placeholder="Client's full name"
            required
            autoComplete="off"
          />

          {/* Suggestions dropdown for name field */}
          {showSuggestions && activeField === 'name' && (
            <div className="customer-suggestions-dropdown">
              {isLoadingSuggestions ? (
                <div className="suggestion-item loading">Searching...</div>
              ) : customerSuggestions.length > 0 ? (
                customerSuggestions.map((customer) => (
                  <div
                    key={customer.ID}
                    className="suggestion-item-wrapper"
                  >
                    <div
                      className="suggestion-item"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handleCustomerSelect(customer)}
                    >
                      <div className="suggestion-name">
                        {customer.CustomerFullName || `${customer.CustomerFirstName} ${customer.CustomerLastName}`.trim()}
                      </div>
                      <div className="suggestion-details">
                        {customer.CustomerPhone && <span>📞 {customer.CustomerPhone}</span>}
                        {customer.CustomerEmail && <span>✉️ {customer.CustomerEmail}</span>}
                      </div>
                    </div>
                    <button
                      className="suggestion-profile-btn"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => window.open(`/customers/${customer.ID}`, '_blank')}
                      title="View customer profile"
                    >
                      <ExternalLink size={16} />
                    </button>
                  </div>
                ))
              ) : (
                <div className="suggestion-item no-results">No customers found</div>
              )}
            </div>
          )}
        </div>


      </div>

      {/* Right side - DiniCare Image */}
      <div className="dini-care-image-section">
        <label className="appointment-form-label">
          DiniCare Customer
        </label>
        <div className="dini-care-image-wrapper-large">
          <img 
            src="/images/dini_cares.gif" 
            alt="DiniCare Customer" 
            className="dini-care-image-large"
            onError={(e) => console.log('Image failed to load:', e)}
            onLoad={() => console.log('Image loaded successfully')}
          />
        </div>
      </div>
    </div>
  </div>
) : (
  <>
    {/* When no DiniCare, show fields normally in grid */}
    <div className="appointment-form-group full-width">
      <label className="appointment-form-label required">
        <FileText size={16} />
        Event Description
      </label>
      <textarea
        className="appointment-form-textarea"
        value={formData.EventDesc}
        onChange={(e) => handleInputChange('EventDesc', e.target.value)}
        placeholder="Brief description of the appointment"
        rows={2}
      />
    </div>

    {/* Client Name */}
    <div className="appointment-form-group" style={{ position: 'relative' }}>
      <label className="appointment-form-label required">
        <User size={16} />
        Client Name
      </label>
      <input
        type="text"
        className="appointment-form-input"
        value={formData.ClientName}
        onChange={(e) => {
          setFormData(prev => ({ ...prev, ClientName: e.target.value, ClientID: '', PhoneNum: '' }));
          setActiveField('name');
        }}
        onBlur={() => {
          setTimeout(() => {
            setShowSuggestions(false);
            setActiveField(null);
          }, 200);
        }}
        onFocus={() => setActiveField('name')}
        placeholder="Client's full name"
        required
        autoComplete="off"
      />
      
      {/* Suggestions dropdown for name field */}
      {showSuggestions && activeField === 'name' && (
        <div className="customer-suggestions-dropdown">
          {isLoadingSuggestions ? (
            <div className="suggestion-item loading">Searching...</div>
          ) : customerSuggestions.length > 0 ? (
            customerSuggestions.map((customer) => (
              <div
                key={customer.ID}
                className="suggestion-item-wrapper"
              >
                <div
                  className="suggestion-item"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleCustomerSelect(customer)}
                >
                  <div className="suggestion-name">
                    {customer.CustomerFullName || `${customer.CustomerFirstName} ${customer.CustomerLastName}`.trim()}
                  </div>
                  <div className="suggestion-details">
                    {customer.CustomerPhone && <span>📞 {customer.CustomerPhone}</span>}
                    {customer.CustomerEmail && <span>✉️ {customer.CustomerEmail}</span>}
                  </div>
                </div>
                <button
                  className="suggestion-profile-btn"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => window.open(`/customers/${customer.ID}`, '_blank')}
                  title="View customer profile"
                >
                  <ExternalLink size={16} />
                </button>
              </div>
            ))
          ) : (
            <div className="suggestion-item no-results">No customers found</div>
          )}
        </div>
      )}
    </div>


  </>
)}
  {/* Phone Number */}
    <div className="appointment-form-group" style={{ position: 'relative' }}>
      <label className="appointment-form-label">
        <Phone size={16} />
        Phone Number
      </label>
      <input
        type="tel"
        className="appointment-form-input"
        value={formData.PhoneNum}
        onChange={(e) => {
          handleInputChange('PhoneNum', e.target.value);
          setActiveField('phone');
        }}
        onBlur={() => {
          setTimeout(() => {
            setShowSuggestions(false);
            setActiveField(null);
          }, 200);
        }}
        onFocus={() => setActiveField('phone')}
        placeholder="(555) 123-4567"
        autoComplete="off"
      />
      
      {/* Suggestions dropdown for phone field */}
      {showSuggestions && activeField === 'phone' && (
        <div className="customer-suggestions-dropdown">
          {isLoadingSuggestions ? (
            <div className="suggestion-item loading">Searching...</div>
          ) : customerSuggestions.length > 0 ? (
            customerSuggestions.map((customer) => (
              <div
                key={customer.ID}
                className="suggestion-item-wrapper"
              >
                <div
                  className="suggestion-item"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleCustomerSelect(customer)}
                >
                  <div className="suggestion-name">
                    {customer.CustomerFullName || `${customer.CustomerFirstName} ${customer.CustomerLastName}`.trim()}
                  </div>
                  <div className="suggestion-details">
                    {customer.CustomerPhone && <span>📞 {customer.CustomerPhone}</span>}
                    {customer.CustomerEmail && <span>✉️ {customer.CustomerEmail}</span>}
                  </div>
                </div>
                <button
                  className="suggestion-profile-btn"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => window.open(`/customers/${customer.ID}`, '_blank')}
                  title="View customer profile"
                >
                  <ExternalLink size={16} />
                </button>
              </div>
            ))
          ) : (
            <div className="suggestion-item no-results">No customers found</div>
          )}
        </div>
      )}
    </div>
{/* Client ID - always show */}
<div className="appointment-form-group">
  <label className="appointment-form-label">
    Client ID
  </label>
  <input
    type="text"
    className="appointment-form-input"
    value={formData.ClientID}
    onChange={(e) => handleInputChange('ClientID', e.target.value)}
    placeholder="Customer number"
    readOnly
  />
  {/* New Customer Indicator */}
  {!formData.ClientID && formData.ClientName && formData.PhoneNum && (
    <div style={{
      marginTop: '8px',
      padding: '8px 12px',
      backgroundColor: 'var(--dark-elevated)',
      border: '2px solid var(--green-accent)',
      borderRadius: '6px',
      fontSize: '12px',
      color: 'var(--green-light)',
      display: 'flex',
      alignItems: 'center',
      gap: '6px'
    }}>
      <User size={14} />
      <span>New customer will be created</span>
    </div>
  )}
  {!formData.ClientID && formData.ClientName && !formData.PhoneNum && (
    <div style={{
      marginTop: '8px',
      padding: '8px 12px',
      backgroundColor: 'var(--dark-elevated)',
      border: '2px solid var(--mfg-dini-pink)',
      borderRadius: '6px',
      fontSize: '12px',
      color: 'var(--mfg-dini-pink-light)',
      display: 'flex',
      alignItems: 'center',
      gap: '6px'
    }}>
      <AlertCircle size={14} />
      <span>Phone number required to create new customer</span>
    </div>
  )}
</div>



            {/* Appointment Title */}
            <div className="appointment-form-group">
              <label className="appointment-form-label required">
                Appointment Type
              </label>
              <select
                className="appointment-form-select"
                value={formData.Title}
                onChange={(e) => handleInputChange('Title', e.target.value)}
                required
              >
                <option value="">Select type...</option>
                {APPOINTMENT_TITLES.map(title => (
                  <option key={title} value={title}>{title}</option>
                ))}
              </select>
            </div>

            {/* Order Number - hidden in woeMode (order doesn't exist yet; linked after submission) */}
            {!woeMode && (
            <div className="appointment-form-group">
              <label className="appointment-form-label">
                Order Number
              </label>
              {!formData.ClientID ? (
                <div style={{ fontSize: '13px', color: 'var(--text-secondary)', padding: '8px 0' }}>
                  Select a customer first to see their orders.
                </div>
              ) : isLoadingSendOutOrders ? (
                <div style={{ fontSize: '13px', color: 'var(--text-secondary)', padding: '8px 0' }}>
                  Loading orders...
                </div>
              ) : (
                <select
                  className="appointment-form-select"
                  value={formData.orderNo}
                  onChange={(e) => handleInputChange('orderNo', e.target.value)}
                >
                  <option value="">— None —</option>
                  {/* If current orderno isn't in the fetched list (e.g. outside top 10), show it anyway */}
                  {/* {formData.orderNo && !sendOutOrders.some(o => String(o.orderno) === String(formData.orderNo)) && (
                    <option value={String(formData.orderNo)}>#{formData.orderNo} (current)</option>
                  )} */}
                  {sendOutOrders.map(order => (
                    <option key={order.orderno} value={String(order.orderno)}>
                      #{order.orderno}{order.orderwigtype ? ` — ${order.orderwigtype}` : ''}{order.OrderDate ? ` (${new Date(order.OrderDate).toLocaleDateString()})` : ''}
                    </option>
                  ))}
                  {sendOutOrders.length === 0 && (
                    <option disabled value="">No orders found for this customer</option>
                  )}
                </select>
              )}
            </div>
            )}

            {/* Worker Responsible - only for Send Out */}
            {formData.Title === 'Send Out' && (
              <div className="appointment-form-group">
                <label className="appointment-form-label required">
                  Worker Responsible for Shipping
                </label>
                <select
                  className="appointment-form-select"
                  value={formData.workerResponsible}
                  onChange={(e) => handleInputChange('workerResponsible', e.target.value)}
                >
                  <option value="">Select worker...</option>
                  {distributorUsers.map(u => (
                    <option key={u.username} value={u.username}>{u.username}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Meeting With */}
            <div className="appointment-form-group">
                <label className="appointment-form-label required">
                    Meeting With
                </label>
                <select
                    className="appointment-form-select"
                    value={formData.MeetingWith}
                    onChange={(e) => handleMeetingWithChange(e.target.value)}
                    required
                >
                    <option value="">Select staff member...</option>
                    {meetingWithOptions.map(option => (
                    <option key={option.id} value={option.name}>
                        {option.name}
                    </option>
                    ))}
                </select>
            </div>

         {/* Start Date/Time */}
<div className="appointment-form-group">
  <label className="appointment-form-label required">
    <Calendar size={16} />
    Start Date & Time
  </label>
  <CustomDateTimePicker
    value={formData.EventStart}
    onChange={(value) => handleStartTimeChange(value)}
    useMilitaryTime={useMilitaryTime}
    label="Start Date & Time"
  />
  {isPastAppointment && (
    <div style={{
      marginTop: '8px',
      padding: '8px 12px',
      backgroundColor: 'var(--dark-elevated)',
      border: '2px solid #f59e0b',
      borderRadius: '6px',
      fontSize: '12px',
      color: '#fbbf24',
      display: 'flex',
      alignItems: 'center',
      gap: '6px'
    }}>
      <AlertCircle size={14} />
      <span>Heads up — this date has already passed. You can still book it, just double-check the date.</span>
    </div>
  )}
  {isLateDiniAppointment && (
    <div role="alert" style={{
      marginTop: '8px',
      padding: '8px 12px',
      backgroundColor: 'var(--dark-elevated)',
      border: '2px solid var(--mfg-dini-pink)',
      borderRadius: '6px',
      fontSize: '12px',
      color: 'var(--mfg-dini-pink-light)',
      display: 'flex',
      alignItems: 'center',
      gap: '6px'
    }}>
      <AlertCircle size={14} />
      <span>{canBookLateDini
        ? "Dini isn't available after 3 PM. Only book this if you're sure."
        : "Dini isn't available after 3 PM. Please pick an earlier slot — only GKirsh, DidiP, or ChenW can book Dini at this time."}</span>
    </div>
  )}
</div>
  {/* Show customer balance using the same component as CustomerProfile */}
    {customerOrders.length > 0 && (
      <div className="customer-balance-display">
        <BatchedTotalBalanceDisplay batchedBalances={customerBalances} />
      </div>
    )}

          {/* End Date/Time */}
<div className="appointment-form-group">
  <label className="appointment-form-label required">
    <Clock size={16} />
    End Date & Time
  </label>
  <CustomDateTimePicker
    value={formData.EventEnd}
    onChange={(value) => handleInputChange('EventEnd', value)}
    useMilitaryTime={useMilitaryTime}
    label="End Date & Time"
  />
</div>

<div className="appointment-form-group">
   {/* Customer Notes Display */}
      <div className="appointment-form-group">
        {customerNotes && (
          <div className="appointment-form-group" >                        
            <label className="appointment-form-label">
              Customer Notes
            </label>
            <div style={{
              color: 'var(--error-color)',
              fontSize: '25px',
              lineHeight: '1',
              whiteSpace: 'pre-wrap'
            }}>
              {customerNotes}
            </div>
          </div>
        )}
      </div>
</div>

{/* Appointment Options */}
<div className="appointment-form-group"> 
  <label className="appointment-form-label">
    Appointment Options
  </label>
  <div className="checkbox-group">
    {/* <label className="checkbox-label">
      <input
        type="checkbox"
        checked={formData.isDiniReq}
        onChange={(e) => handleInputChange('isDiniReq', e.target.checked)}
      />
      Dini Required
    </label> */}
    <label className="checkbox-label">
      <input
        type="checkbox"
        checked={formData.isReadyToPickup}
        onChange={(e) => handleInputChange('isReadyToPickup', e.target.checked)}
      />
      Ready to Pickup
    </label>
    <label className="checkbox-label">
      <input
        type="checkbox"
        checked={formData.bDiniCare === 1 || formData.bDiniCare === true}  // ✅ CORRECT
        onChange={(e) => handleDiniCareChange(e.target.checked)}  // ✅ CORRECT
        disabled={!formData.ClientID}
        title={!formData.ClientID ? "Select a customer first" : "Update customer's Dini Care status"}
      />
      Dini Care {!formData.ClientID && "(Select customer first)"}
    </label>
 
      {formData.isReadyToPickup && (
        <div style={{ display: 'flex', alignItems: 'center', marginLeft: '30px' }}>
          <ShoppingBag   
            size={40}
            className="pickup-icon" 
            style={{ color: '#10b981' }} 
            title="Ready to Pickup"
          />
        </div>
      )}
         {/* Status-based icons */}
    {formData.AppointmentStatus === 'Confirmed' && (
      <CalendarCheck size={40} style={{ marginLeft: '8px', color: '#059669' }} title="Confirmed" />
    )}
    {formData.AppointmentStatus === 'Called' && (
      <PhoneCall size={40} style={{ marginLeft: '8px', color: '#0ea5e9' }} title="Called" />
    )}
    {(formData.AppointmentStatus === 'Cancelled' || formData.AppointmentStatus === 'No Show') && (
      <Ban size={40} style={{ marginLeft: '8px', color: '#dc2626' }} title={formData.AppointmentStatus} />
    )}
    {formData.AppointmentStatus === 'Reschedule Requested' && (
      <CalendarSync size={40} style={{ marginLeft: '8px', color: '#f59e0b' }} title="Reschedule Requested" />
    )}
  </div>
      {isEditing && editingAppointment?.dtInserted && (
    <div className="appointment-created-info">
      <small style={{ color: 'var(--text-primary)', marginRight: '16px', fontSize: '18px' }}>
        Created: {formatDateTimeForUser(editingAppointment.dtInserted, currentUser)} By: {editingAppointment.EnteredBy || 'N/A'}
      </small>
    </div>
  )}
  </div>

            {/* Notes */}
            <div className="appointment-form-group full-width">
              <label className="appointment-form-label">
                Additional Notes
              </label>
              <textarea
                className="appointment-form-textarea"
                value={formData.AppointmentNotes}
                onChange={(e) => handleInputChange('AppointmentNotes', e.target.value)}
                placeholder="Any additional notes or special requirements for the appointment. Please update customer notes in their profile."
                rows={3}
              />
            </div>
          </div>

          {/* Status Messages */}
          {submitStatus === 'success' && (
            <div className="appointment-status-message success">
              <CheckCircle size={20} />
              Appointment {isEditing ? 'updated' : 'created'} successfully!
            </div>
          )}

          {noCardError && (
            <div className="appointment-status-message error" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <CreditCard size={20} />
                <strong>No card on file for this customer.</strong>
              </div>
              <div style={{ fontSize: '13px' }}>
                {isEditing
                  ? 'A card on file is required for Send Out appointments. Please add a card, or check the box below to save your changes anyway (you will be reminded to add one).'
                  : 'A card on file is required to create a Send Out appointment. Please add a card first, then try again.'}
              </div>
              <button
                type="button"
                onClick={handleOpenSaveCardModal}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 14px',
                  backgroundColor: 'var(--mfg-dini-pink, #e91e8c)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '600'
                }}
              >
                <CreditCard size={14} />
                Add Card for Customer
              </button>
              {(currentUser?.UserName || currentUser?.username)?.toLowerCase() === 'ettiw' && (
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#e7d5d5', cursor: 'pointer', marginTop: '2px' }}>
                  <input
                    type="checkbox"
                    checked={bypassCardCheck}
                    onChange={(e) => setBypassCardCheck(e.target.checked)}
                    style={{ accentColor: '#dc2626', width: '15px', height: '15px', cursor: 'pointer' }}
                  />
                  <span>Proceed without card (you will be reminded to add one)</span>
                </label>
              )}
            </div>
          )}

          {(submitStatus === 'error' || errorMessage) && !noCardError && (
            <div className="appointment-status-message error">
              <AlertCircle size={20} />
              {errorMessage}
            </div>
          )}

         {/* Form Actions */}
          <div className="appointment-form-actions">
            {isEditing && (
              <button 
                type="button" 
                onClick={() => setShowDeleteConfirm(true)}
                className="appointment-modal-cancel-btn"
                disabled={isSubmitting}
              >
                Cancel Appointment
              </button>
            )}


            
            {/* Show View Customer / View Order buttons when data is available */}
            {(formData.ClientID || formData.orderNo) && (
            <div className="customer-info-section">
              {formData.ClientID && (
                <button
                  type="button"
                  className="appointment-btn appointment-btn-secondary"
                  onClick={handleViewCustomer}
                  disabled={isSubmitting || isDeleting}
                >
                  View Customer
                </button>
              )}
              {formData.orderNo && (
                <button
                  type="button"
                  className="appointment-btn appointment-btn-secondary"
                  onClick={() => window.open(`/work-order-profile/${formData.orderNo}`, '_blank')}
                  disabled={isSubmitting || isDeleting}
                >
                  View Order
                </button>
              )}
            </div>
          )}
            <button
              type="button"
              className="appointment-btn appointment-btn-primary"
              onClick={handleSubmit}
              disabled={isSubmitting || isDeleting}
            >
              {isSubmitting 
                ? (isEditing ? 'Updating...' : 'Creating...') 
                : (isEditing ? 'Update Appointment' : 'Create Appointment')
              }
            </button>
          </div>

            {/* Delete Confirmation Dialog */}
            {showDeleteConfirm && (
              <div className="delete-confirm-dialog">
                <p>Are you sure you want to cancel this appointment?</p>
                <div className="delete-confirm-buttons">
                  <button 
                    onClick={handleCancelAppointment}
                    className="appointment-modal-cancel-btn"
                    disabled={isDeleting}
                  >
                    {isDeleting ? 'Cancelling...' : 'Yes, Cancel'}
                  </button>
                  <button 
                    onClick={() => setShowDeleteConfirm(false)}
                    className="cancel-delete-btn"
                    disabled={isDeleting}
                  >
                    No, Keep it
                  </button>
                </div>
              </div>
            )}
        </div>
      </div>
    </div>,
    document.body // Render at document.body level
  ),

  // SaveCardModal rendered outside the appointment portal so it sits on top
  showSaveCardModal && createPortal(
    <SaveCardModal
      customerId={formData.ClientID}
      currentUsername={currentUser}
      onClose={() => {
        setShowSaveCardModal(false);
        setNoCardError(false);
      }}
      onCardSaved={() => {
        setShowSaveCardModal(false);
        setNoCardError(false);
      }}
    />,
    document.body
  )
];
};

export default CreateAppointmentModal;